// ============================================================================
// JARVISTRAVEL - ONBOARDING TOUR STATE
// Seen-state seam for the first-run coach-mark tour (JAR-263). Local-first:
// the pending-flow flag and seen keys persist in localStorage so the tour
// never repeats. A demo keeps its own, in sessionStorage: it gets the full
// tour on top of sample data, and that tour is the demo's, not the browser's
// (JAR-1850). Model-ready: when the backend exposes onboarding state on
// /preferences, mirror writes there (same shape as preferences.ts) so seen
// state follows the account across devices. TODO(backend): JT-OBT-001.
//
// Design note (PR #74 review, M2): this is deliberately NOT a React
// context/store. Each key has exactly one writer at one moment — the pending
// flag is written at the onboarding fork (before Dashboard mounts) and
// consumed once; each seen key is written by the single surface that owns it
// (tour end, or one tip's dismissal on its own tab). No component reacts to
// another's writes, so there is no shared-state tear to guard against.
// Storage eviction (Safari ITP, cleared site data) degrades to the designed
// worst case: the tour offers itself once more. Cross-device durability is
// the backend mirror above, not an in-memory layer.
// ============================================================================

import { isDemoSession } from './client';

/**
 * Where this tour state lives. A demo is a static example connected to no
 * account (JAR-1846), so what its tour marks as seen must not be seen for
 * whoever signs up next in this browser (JAR-1850). sessionStorage rather than
 * memory, so a reload inside the demo does not start the tour over.
 *
 * Read at each call, never captured: a demo starts and ends while this module
 * stays loaded, and the state each write belongs to is decided at the write.
 */
function store(): Storage {
  return isDemoSession() ? sessionStorage : localStorage;
}

/** Which first-run tour the onboarding fork queued: built a trip, or explore-first. */
export type TourFlow = 'trip' | 'explore';

export const PENDING_STORAGE_KEY = 'jarvis_tour_pending';
/** Versioned so a future tour redesign can re-run without fighting stale state. */
export const SEEN_STORAGE_KEY = 'jarvis_onboarding_v1';

/** Queue a tour at the onboarding fork (planner built → trip; skipped → explore). */
export function setPendingTour(flow: TourFlow): void {
  try {
    store().setItem(PENDING_STORAGE_KEY, flow);
  } catch {
    /* storage unavailable — tour is best-effort, never blocks the app */
  }
}

/** The queued tour, if any. Unknown values (old builds, tampering) read as null. */
export function getPendingTour(): TourFlow | null {
  try {
    const raw = store().getItem(PENDING_STORAGE_KEY);
    return raw === 'trip' || raw === 'explore' ? raw : null;
  } catch {
    return null;
  }
}

export function clearPendingTour(): void {
  try {
    store().removeItem(PENDING_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

function readSeen(): Record<string, true> {
  try {
    const raw = store().getItem(SEEN_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, true>;
    }
  } catch {
    /* corrupted state reads as nothing-seen; next markSeen rewrites it */
  }
  return {};
}

/** Whether a tour or contextual tip was already shown. Dismissal is permanent. */
export function isSeen(key: string): boolean {
  return readSeen()[key] === true;
}

/**
 * Queue a tour only if its flow hasn't run yet — repeat entries (demo logins,
 * re-visiting the plan gate) stay quiet. Settings replay bypasses this on
 * purpose by calling setPendingTour directly.
 */
export function queueTourIfUnseen(flow: TourFlow): void {
  if (!isSeen(`tour_${flow}`)) setPendingTour(flow);
}

/** Record a tour/tip as shown (e.g. 'tour_trip', 'tip_explore_activity'). */
export function markSeen(key: string): void {
  try {
    store().setItem(SEEN_STORAGE_KEY, JSON.stringify({ ...readSeen(), [key]: true }));
  } catch {
    /* storage unavailable — worst case the tip shows again next session */
  }
}
